import React, { useState, useEffect, useCallback } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  Title,
} from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import {
  RecurringEvent,
  RecurringEventInput,
  apiDateStringToDate,
  apiTimeToFormTime,
  isValidTimeFormat,
  compareTimeStrings,
  dateToApiDateString,
  formTimeToApiTime,
  Room,
  RecurringEventTeam,
  RecurringEventTeamInput,
  omit,
  EventTeamInput,
} from "@/utils";
import {
  AppModal,
  AppIcon,
  RecurrenceDaysSelector,
  RecurrenceSelector,
} from "@/components/generic";

import { RoomSelector } from "../Rooms";
import { ClientSelector } from "../Clients";
import { StatusSelector } from "../StatusSelector";
import { EventTeamSelector } from "../EventTeamSelector";
import { EventexternalUrlTypeSelector } from "../EventexternalUrlTypeSelector";
import { CategoriesInput } from "../Categories";
import { useCategoriesContext } from "@/contexts";

// Enable dayjs plugins
dayjs.extend(customParseFormat);

export interface RecurringEventFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Recurring event to edit (if null/undefined, it's create mode) */
  recurringEvent?: RecurringEvent | null;

  /** Venue ID for new events */
  venueId?: string;

  /** Callback when recurring event should be created */
  onCreateRecurringEvent?: (
    eventData: RecurringEventInput
  ) => Promise<RecurringEvent | null>;

  /** Callback when recurring event should be updated */
  onUpdateRecurringEvent?: (
    recurringEventId: string,
    eventData: RecurringEventInput
  ) => Promise<RecurringEvent | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from event operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface RecurringEventFormData {
  name: string;
  description: string;
  recurrencePattern: string;
  daysOfWeek: string[];
  startDate: Date | null;
  endDate: Date | null;
  startTime: string;
  endTime: string;
  color: string;
  categories?: string[] | null | undefined;
  imageUploadedFileId?: string | null | undefined;
  imageUrl?: string | null | undefined;
  externalUrlType?: string | null | undefined;
  externalUrl?: string | null | undefined;
  status: number;
  roomId: string;
  clientId: string;
  recurringEventTeams: RecurringEventTeamInput[];
  rooms: Room[];
}

const initialFormData: RecurringEventFormData = {
  name: "",
  description: "",
  recurrencePattern: "WEEKLY",
  daysOfWeek: [],
  startDate: null,
  endDate: null,
  startTime: "",
  endTime: "",
  color: "#3b82f6",
  categories: [],
  imageUrl: null,
  externalUrlType: null,
  externalUrl: null,
  imageUploadedFileId: null,
  status: 100,
  roomId: "",
  clientId: "",
  recurringEventTeams: [],
  rooms: [],
};

export const RecurringEventFormModal: React.FC<
  RecurringEventFormModalProps
> = ({
  opened,
  onClose,
  recurringEvent,
  venueId,
  onCreateRecurringEvent,
  onUpdateRecurringEvent,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<RecurringEventFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const { ensureCategories } = useCategoriesContext();

  const isEditMode = Boolean(recurringEvent);
  const isSubmitting = isCreating || isUpdating;

  const prepareOutTeam = useCallback(
    (team: RecurringEventTeam): RecurringEventTeamInput => {
      return {
        ...omit(team as any, ["__typename", "id"]),
        supportingRooms:
          team.supportingRooms?.map((room) => room.id ?? "").filter(Boolean) ??
          [],
      };
    },
    []
  );

  // Reset form when modal opens/closes or event changes
  useEffect(() => {
    if (opened) {
      if (recurringEvent) {
        // Edit mode - populate form with event data
        setFormData({
          name: recurringEvent.name || "",
          description: recurringEvent.description || "",
          recurrencePattern: recurringEvent.recurrencePattern || "WEEKLY",
          daysOfWeek: recurringEvent.daysOfWeek
            ? recurringEvent.daysOfWeek.split(",").map((d) => d.trim())
            : [],
          startDate: apiDateStringToDate(recurringEvent.startDate),
          endDate: apiDateStringToDate(recurringEvent.endDate),
          startTime: apiTimeToFormTime(recurringEvent.startTime),
          endTime: apiTimeToFormTime(recurringEvent.endTime),
          color: recurringEvent.color || "#3b82f6",
          categories: recurringEvent.categories?.map(
            (category) => category.name ?? ""
          ),
          imageUploadedFileId: recurringEvent.imageUploadedFileId,
          imageUrl: recurringEvent.imageUrl,
          externalUrlType: recurringEvent.externalUrlType,
          externalUrl: recurringEvent.externalUrl,
          status: recurringEvent.status != null ? recurringEvent.status : 100,
          roomId: recurringEvent.roomId || "",
          clientId: recurringEvent.clientId || "",
          recurringEventTeams:
            recurringEvent.recurringEventTeams?.map((recurrinEventTeam) =>
              prepareOutTeam(recurrinEventTeam)
            ) || [],
          rooms: recurringEvent.rooms ?? [],
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, recurringEvent, onClearError]);

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Event name is required";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      dayjs(formData.startDate).isAfter(dayjs(formData.endDate))
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    if (!formData.startTime?.trim()) {
      newErrors.startTime = "Start time is required";
    } else if (!isValidTimeFormat(formData.startTime)) {
      newErrors.startTime = "Please enter a valid time (e.g., 10:00 or 22:00)";
    }

    if (!formData.endTime?.trim()) {
      newErrors.endTime = "End time is required";
    } else if (!isValidTimeFormat(formData.endTime)) {
      newErrors.endTime = "Please enter a valid time (e.g., 11:00 or 23:00)";
    }

    // Time validation
    if (
      formData.startTime &&
      formData.endTime &&
      isValidTimeFormat(formData.startTime) &&
      isValidTimeFormat(formData.endTime) &&
      !compareTimeStrings(formData.startTime, formData.endTime)
    ) {
      newErrors.endTime = "End time must be after start time";
    }

    if (
      formData.recurrencePattern === "WEEKLY" &&
      formData.daysOfWeek.length === 0
    ) {
      newErrors.daysOfWeek =
        "At least one day must be selected for weekly recurrence";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof RecurringEventFormData,
    value: any
  ) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      return newData;
    });

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle client selection change
  const handleClientChange = (clientId: string | string[] | null) => {
    const selectedClientId =
      (Array.isArray(clientId) ? clientId[0] : clientId) || "";
    handleInputChange("clientId", selectedClientId);
  };

  // Handle main room selection change
  // const handleMainRoomChange = (roomId: string | string[] | null) => {
  //   const selectedRoomId = (Array.isArray(roomId) ? roomId[0] : roomId) || "";
  //   handleInputChange("roomId", selectedRoomId);
  // };

  const handleRoomsChange = (
    _: string | string[] | null,
    rooms?: Room | Room[]
  ) => {
    handleInputChange("rooms", rooms);
  };

  // Handle event teams change
  const handleEventTeamsChange = (eventTeams: EventTeamInput[]) => {
    handleInputChange(
      "recurringEventTeams",
      eventTeams.map((eventTeam) => {
        return Object.assign({}, omit(eventTeam, ["eventId"]), {
          recurringEventId: eventTeam.eventId,
        });
      })
    );
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof RecurringEventFormData
  ): string | undefined => {
    return validationErrors[field] as string;
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare event data - convert Date and time back to API format
      const eventData: RecurringEventInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        recurrencePattern: formData.recurrencePattern,
        daysOfWeek:
          formData.daysOfWeek.length > 0
            ? formData.daysOfWeek.join(",")
            : undefined,
        startDate: dateToApiDateString(formData.startDate),
        endDate: dateToApiDateString(formData.endDate),
        startTime: formTimeToApiTime(formData.startTime),
        endTime: formTimeToApiTime(formData.endTime),
        color: formData.color,
        categories: formData.categories,
        imageUploadedFileId: formData.imageUploadedFileId,
        externalUrlType: formData.externalUrlType,
        externalUrl: formData.externalUrl,
        status: formData.status,
        venueId: venueId ?? "",
        roomId: formData.roomId || null,
        clientId: formData.clientId || null,
        recurringEventTeams: formData.recurringEventTeams,
        rooms: formData.rooms.map((room) => room.id || ""),
      };

      let result: RecurringEvent | null = null;

      if (isEditMode && recurringEvent && onUpdateRecurringEvent) {
        // Update existing event
        result = await onUpdateRecurringEvent(recurringEvent.id, eventData);
      } else if (!isEditMode && onCreateRecurringEvent) {
        // Create new event
        result = await onCreateRecurringEvent(eventData);
      }

      // If successful, close the modal
      if (result) {
        ensureCategories(result.categories);
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  const getRecurrenceDaysValue = (): string[] => {
    if (!formData.daysOfWeek) return [];
    return formData.daysOfWeek.filter(Boolean);
  };

  const handleRecurrenceDaysChange = (values: string[]) => {
    handleInputChange("daysOfWeek", values);
  };

  // Render recurrence pattern specific fields
  const renderRecurrenceFields = () => {
    if (["WEEKLY", "MONTHLY"].includes(formData.recurrencePattern)) {
      return (
        <RecurrenceDaysSelector
          recurrencePattern={formData.recurrencePattern}
          value={getRecurrenceDaysValue()}
          onChange={handleRecurrenceDaysChange}
          error={getDisplayError("daysOfWeek")}
        />
      );
    }

    if (formData.recurrencePattern === "YEARLY") {
      return (
        <Text size="sm" c="dimmed">
          Event will occur on the same date every year within the date range.
        </Text>
      );
    }

    return null;
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Recurring Event" : "Add New Recurring Event"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Basic Information */}
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Event Name"
              placeholder="Enter event name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              required
              withAsterisk
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Description"
              placeholder="Enter event description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              error={getDisplayError("description")}
              rows={2}
            />
          </Grid.Col>
        </Grid>

        {/* Date Range */}
        <Grid>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <DatePickerInput
              valueFormat="MMM DD, YYYY"
              label="Start Date"
              placeholder="Select start date"
              value={formData.startDate}
              onChange={(value) => handleInputChange("startDate", value)}
              error={getDisplayError("startDate")}
              required
              withAsterisk
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 6 }}>
            <DatePickerInput
              valueFormat="MMM DD, YYYY"
              label="End Date"
              placeholder="Select end date"
              value={formData.endDate}
              onChange={(value) => handleInputChange("endDate", value)}
              error={getDisplayError("endDate")}
              required
              withAsterisk
              clearable
            />
          </Grid.Col>
        </Grid>

        {/* Time Configuration */}
        <Grid>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <TimeInput
              label="Start Time"
              placeholder="10:00"
              value={formData.startTime}
              onChange={(e) => handleInputChange("startTime", e.target.value)}
              error={getDisplayError("startTime")}
              required
              withAsterisk
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 6 }}>
            <TimeInput
              label="End Time"
              placeholder="23:00"
              value={formData.endTime}
              onChange={(e) => handleInputChange("endTime", e.target.value)}
              error={getDisplayError("endTime")}
              required
              withAsterisk
            />
          </Grid.Col>
        </Grid>

        {/* Recurrence Pattern */}
        <Grid>
          <Grid.Col span={12}>
            <RecurrenceSelector
              label="Recurrence Pattern"
              placeholder="Select recurrence"
              value={formData.recurrencePattern}
              onChange={(value) =>
                handleInputChange("recurrencePattern", value)
              }
              error={getDisplayError("recurrencePattern")}
              required
            />
          </Grid.Col>

          <Grid.Col span={12}>{renderRecurrenceFields()}</Grid.Col>
        </Grid>

        {/* Location Configuration */}
        {/* {venueId && (
          <Grid>
            <Grid.Col span={12}>
              <Title order={5} mb="sm">
                Room Assignment
              </Title>
              <RoomSelector
                label="Main Room"
                placeholder="Select the main room for this event (optional)"
                venueId={venueId}
                value={formData.roomId || null}
                onChange={handleMainRoomChange}
                error={getDisplayError("roomId")}
                clearable
                size="sm"
                multiple={false}
              />
            </Grid.Col>
          </Grid>
        )} */}

        {venueId && (
          <Grid>
            <Grid.Col span={12}>
              <RoomSelector
                label="Event Rooms"
                placeholder="Select rooms for this event (optional)"
                venueId={venueId}
                value={formData.rooms
                  .map((room) => room.id || "")
                  .filter((id) => !!id)}
                onChange={handleRoomsChange}
                error={getDisplayError("rooms")}
                clearable
                multiple={true}
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Event Teams */}
        {venueId && (
          <Grid>
            <Grid.Col span={12}>
              <Title order={5} mb="sm">
                Event Teams
              </Title>
              <EventTeamSelector
                value={formData.recurringEventTeams.map(
                  (recurringEventTeam) => {
                    return Object.assign(
                      {},
                      omit(recurringEventTeam, ["recurringEventId"]),
                      { eventId: recurringEventTeam.recurringEventId }
                    );
                  }
                )}
                onChange={handleEventTeamsChange}
                venueId={venueId}
                placeholder="No teams added yet. Teams help organize supporting rooms for your event."
                error={getDisplayError("recurringEventTeams")}
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Client Selection */}
        <Grid>
          <Grid.Col span={12}>
            <ClientSelector
              label="Client"
              placeholder="Select client for this event (optional)"
              value={formData.clientId || null}
              onChange={handleClientChange}
              error={getDisplayError("clientId")}
              clearable
              multiple={false}
            />
          </Grid.Col>
        </Grid>

        {/* Tags */}
        <Grid>
          <Grid.Col span={12}>
            <CategoriesInput
              label="Categories"
              placeholder="Type and press Enter to add categories"
              value={formData.categories ?? []}
              onChange={(categories) =>
                handleInputChange("categories", categories)
              }
              error={getDisplayError("categories")}
              description="Add categoroes to organize this event (e.g., 'Programs', 'Activities', etc)"
              clearable
              splitChars={[",", ";"]}
              maxTags={20}
            />
          </Grid.Col>
        </Grid>

        {/* Extetrnal URL */}
        <Grid>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <EventexternalUrlTypeSelector
              label="Type"
              value={formData.externalUrlType}
              onChange={(value) => handleInputChange("externalUrlType", value)}
              error={getDisplayError("externalUrlType")}
              required
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <TextInput
              label="External URL"
              type="url"
              placeholder="Enter external event info URL"
              value={formData.externalUrl || ""}
              onChange={(e) => handleInputChange("externalUrl", e.target.value)}
              error={getDisplayError("externalUrl")}
              //description="Use it when you want people to RSVP or Buy tickets on external platform"
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Status */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
